import React, { createContext, useCallback, useContext, useState } from 'react';
import { Drawer, Tooltip } from 'antd';
import ReactJson from '@microlink/react-json-view';
import { useQueryWithStore } from 'react-admin';
import { getInitials } from '../../Common/utils';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';
import CounterpartyMembers from './CounterpartyMembers';
import { CounterpartyPov, CounterpartyTeam, POV_TEAMS } from './types';
import './Counterparty.css';

/** the team the panel is open on, and the side it stands on */
type Target = { id: number; name?: string; pov: CounterpartyPov };

/** what a name needs to be able to open the panel — null where there is none */
const CounterpartyContext = createContext<((team: CounterpartyTeam, pov: CounterpartyPov) => void) | null>(null);

/**
 * Opens the counterparty panel, or null outside a `CounterpartyProvider`.
 *
 * A row that finds none renders its counterparty as text, which is what a list
 * with no panel behind it should look like.
 */
export const useCounterparty = () => useContext(CounterpartyContext);

/** one labelled value in the panel's header block */
const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="cp-fact">
        <span className="cp-fact__label">{label}</span>
        <span className="cp-fact__value">{children}</span>
    </div>
);

/**
 * The counterparty itself: what the team is, then who is on it.
 *
 * Both come from the pov endpoints, which answer for the other party alone —
 * `teams/<pov>_teams/<id>/` for the team and `team/memberships/<pov>/` for its
 * members. A team the caller has no dealing with is not in either, so there is
 * nothing to check here beyond what came back.
 */
const CounterpartyBody = ({ target }: { target: Target }) => {
    const { teams, memberships } = POV_TEAMS[target.pov];

    const { data: team, loaded, error } = useQueryWithStore(
        { type: 'getOne', resource: teams, payload: { id: target.id } },
        /* A 404 here is a real answer, not a fault: `serviceowner_teams` holds
           only teams that still own a service, so a team that has since let go
           of its last one drops out of it while its old changes stay on the
           list. The panel says so in its own words below; the default red
           notification would say it again, in an alarming one. `action` is
           react-admin's own default, restated because passing options replaces
           it and dropping it would cost the query its cache. */
        { action: 'CUSTOM_QUERY', onFailure: () => undefined },
    );

    const metadata = team?.metadata;
    const hasMetadata = !!metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0;

    return (
        <div className="cp-body" data-cy="counterparty-body">
            {/* which side they stand on is the header's line, not a fact here */}
            <div className="cp-facts">
                <Fact label="Team">#{target.id}</Fact>
                <Fact label="Email">
                    {team?.email ? (
                        <a href={`mailto:${team.email}`} onClick={(e) => e.stopPropagation()}>{team.email}</a>
                    ) : '—'}
                </Fact>
                <Fact label="Created">
                    {team?.created ? (
                        <Tooltip title={formatDateAndTime(team.created)}>
                            <span>{formatDateAndTimeFromNow(team.created)}</span>
                        </Tooltip>
                    ) : '—'}
                </Fact>
            </div>

            {/* The name came off the row, so the panel still says who this is
                when the team itself cannot be read — an approval that stands
                unchanged and a panel that says nothing is worse than both. */}
            {loaded && error && (
                <p className="cp-note" role="status">
                    The rest of this team’s details are not available to you.
                </p>
            )}

            {hasMetadata && (
                <section className="cp-section">
                    <div className="cp-section__head">
                        <h4 className="cp-section__title">Metadata</h4>
                    </div>
                    {/* the viewer the team's own Metadata tab reads on */}
                    <div className="cp-json">
                        <ReactJson
                            style={{ padding: '12px', background: 'transparent' }}
                            src={metadata}
                            displayDataTypes={false}
                            name={null}
                            displayArrayKey={false}
                            displayObjectSize={false}
                        />
                    </div>
                </section>
            )}

            <section className="cp-section cp-section--members">
                <CounterpartyMembers resource={memberships} teamId={target.id} />
            </section>
        </div>
    );
};

/**
 * The panel a counterparty's name opens: half the page, sliding in from the
 * right over the list that named them.
 *
 * One per page rather than one per row — the list is long and only ever one
 * team is being read. `CounterpartyName` is what puts a name into it.
 */
export const CounterpartyProvider = ({ children }: { children: React.ReactNode }) => {
    const [target, setTarget] = useState<Target | null>(null);
    // Open is tracked apart from the target so the panel still has something to
    // draw while it slides back out; the target is dropped once it has gone.
    const [open, setOpen] = useState(false);

    const show = useCallback((team: CounterpartyTeam, pov: CounterpartyPov) => {
        if (!team?.id) return;
        setTarget({ id: team.id, name: team.name, pov });
        setOpen(true);
    }, []);

    const name = target?.name;

    return (
        <CounterpartyContext.Provider value={show}>
            {children}

            <Drawer
                rootClassName="cp-drawer"
                classNames={{ header: 'cp-drawer__head', body: 'cp-drawer__panel' }}
                placement="right"
                size="50%"
                /* Above two things that would otherwise sit over it: the top
                   bar, fixed at 1001, which takes the first 56px of the panel
                   and with it the header naming the team; and the tooltip on
                   the row that opened it, at antd's page-level 1070, which the
                   pointer never leaves once the mask is over it and which lands
                   squarely on that same header. Tooltips *inside* the panel
                   still win — antd hands its own z-index down to them. */
                zIndex={1080}
                open={open}
                onClose={() => setOpen(false)}
                afterOpenChange={(opened) => { if (!opened) setTarget(null); }}
                title={target && (
                    <span className="cp-title">
                        <span className="cp-title__badge" aria-hidden="true">
                            {getInitials(name || '?').slice(0, 2)}
                        </span>
                        <span className="cp-title__text">
                            <span className="cp-title__name">{name || `Team #${target.id}`}</span>
                            <span className="cp-title__side">{POV_TEAMS[target.pov].label}</span>
                        </span>
                    </span>
                )}
            >
                {target && <CounterpartyBody key={`${target.pov}:${target.id}`} target={target} />}
            </Drawer>
        </CounterpartyContext.Provider>
    );
};

/**
 * A counterparty's name, as a way into them.
 *
 * Falls back to plain text wherever the panel cannot be opened — outside a
 * provider, on a list with no pov, or on a record whose team came through
 * without an id — so a caller never has to ask which it is getting.
 */
export const CounterpartyName = ({
    team,
    pov,
    label,
    className,
    fallback = '—',
}: {
    team: CounterpartyTeam;
    pov?: CounterpartyPov;
    /** what the column calls this team, for the button's accessible name */
    label: string;
    className?: string;
    fallback?: React.ReactNode;
}) => {
    const show = useCounterparty();
    const name = team?.name;

    if (!name) return <>{fallback}</>;
    if (!show || !pov || !team?.id) return <>{name}</>;

    return (
        <button
            type="button"
            className={`cp-link${className ? ` ${className}` : ''}`}
            data-cy="counterparty-link"
            aria-label={`${label} ${name} — open team`}
            /* The row around this is itself a button that navigates, and the
               keyboard reaches this one through it. Neither event may carry on
               up, or opening the panel also opens the record behind it. */
            onClick={(e) => {
                e.stopPropagation();
                show(team, pov);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
            }}
        >
            {name}
        </button>
    );
};

export default CounterpartyProvider;
